import { useState } from "react";
import { toast } from "sonner";
import { Check, ArrowUpRight, Linkedin } from "lucide-react";
import { api, INTEREST_OPTIONS, LINKEDIN_GROUP_URL } from "@/lib/api";
import { openCalendly } from "@/lib/calendly";

const initial = { name: "", email: "", company: "", message: "" };

export default function GetInvolved({ compact = false }) {
  const [form, setForm] = useState(initial);
  const [interests, setInterests] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (val) => {
    setInterests((prev) =>
      prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    if (interests.length === 0) {
      toast.error("Pick at least one interest.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        interests,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Thanks — we'll be in touch.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Something went wrong.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="get-involved"
      data-testid="get-involved-section"
      className="border-r-0 lg:border-r hairline p-8 md:p-12 lg:p-16 bg-white"
    >
      <div className={`max-w-xl ${compact ? "mx-auto" : ""}`}>
        {!compact && (
          <>
            <div data-testid="get-involved-form-section-label" className="font-mono-label text-[11px] text-[#0F1117] mb-6">
              / Get involved
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight font-extrabold text-[#0F1117] text-balance mb-4">
              Tell us where you fit.
            </h2>
            <p className="text-[15px] text-[#52555B] leading-relaxed mb-10">
              {"One form, unlimited ways in. We'll route your interest to the right person on our side and reply quickly."}
            </p>
          </>
        )}

        {!done ? (
          <form onSubmit={onSubmit} data-testid="get-involved-form" className="space-y-6">
            <Field label="Name *" htmlFor="gi-name">
              <input
                id="gi-name"
                data-testid="gi-name-input"
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
                placeholder="Jane Doe"
              />
            </Field>

            <Field label="Email *" htmlFor="gi-email">
              <input
                id="gi-email"
                data-testid="gi-email-input"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
                placeholder="you@company.com"
              />
            </Field>

            <Field label="Company / Organization" htmlFor="gi-company">
              <input
                id="gi-company"
                data-testid="gi-company-input"
                type="text"
                autoComplete="organization"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className={inputCls}
                placeholder="Optional"
              />
            </Field>

            <fieldset>
              <legend className="font-mono-label text-[11px] text-[#5F6168] mb-3 block">
                What are you interested in? *
              </legend>
              <div className="grid grid-cols-2 gap-px bg-[#D2D5DA] border hairline">
                {INTEREST_OPTIONS.map((opt) => {
                  const active = interests.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="checkbox"
                      aria-checked={active}
                      data-testid={`interest-${opt.value}`}
                      onClick={() => toggle(opt.value)}
                      className={`chrome-tile text-left px-4 py-3.5 text-sm flex items-center gap-2.5 ${
                        active ? "chrome-tile-active" : "text-[#0F1117]"
                      }`}
                    >
                      <span
                        className={`w-3.5 h-3.5 border flex items-center justify-center ${
                          active
                            ? "border-white bg-white"
                            : "border-[#A9ADB4] bg-white"
                        }`}
                      >
                        {active && <Check size={10} className="text-[#0F1117]" />}
                      </span>
                      <span className="font-medium">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <Field label="Tell us a bit more" htmlFor="gi-msg">
              <textarea
                id="gi-msg"
                data-testid="gi-message-input"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${inputCls} resize-none`}
                placeholder="Optional — context, timing, what success looks like."
              />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              data-testid="gi-submit-btn"
              className="chrome-btn-dark w-full inline-flex items-center justify-center gap-2 font-mono-label text-[12px] px-6 py-4 disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit"}
              {!submitting && <ArrowUpRight size={16} />}
            </button>
          </form>
        ) : (
          <ThankYou
            name={form.name}
            onReset={() => {
              setDone(false);
              setForm(initial);
              setInterests([]);
            }}
          />
        )}
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="font-mono-label text-[11px] text-[#5F6168] mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "chrome-surface w-full rounded-none px-4 py-3.5 text-[15px] text-[#0F1117] focus:outline-none focus:border-[#0F1117] focus:ring-1 focus:ring-[#0F1117] transition-colors placeholder:text-[#9498A0]";

function ThankYou({ name, onReset }) {
  return (
    <div data-testid="gi-thank-you" className="fade-up">
      <div className="font-mono-label text-[11px] text-[#0F1117] mb-4">
        / Transmission received
      </div>
      <h3 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0F1117] tracking-tight mb-3">
        {`Thanks${name ? `, ${name.split(" ")[0]}` : ""}.`}
      </h3>
      <p className="text-[15px] text-[#52555B] leading-relaxed mb-8 max-w-md">
        {"We've logged your interest. A confirmation is on its way to your inbox. Two next moves while you're here:"}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#D2D5DA] border hairline">
        <a
          href="/#book"
          onClick={openCalendly}
          data-testid="thank-you-book-btn"
          className="chrome-btn-dark p-6 flex flex-col justify-between min-h-[140px]"
        >
          <ArrowUpRight size={20} />
          <div>
            <div className="font-mono-label text-[10px] text-white/70 mb-1">
              Book now
            </div>
            <div className="font-display text-xl font-bold">Book a meeting</div>
          </div>
        </a>
        <a
          href={LINKEDIN_GROUP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="thank-you-linkedin-btn"
          className="chrome-tile p-6 flex flex-col justify-between min-h-[140px] text-[#0F1117]"
        >
          <Linkedin size={20} className="text-[#0F1117]" />
          <div>
            <div className="font-mono-label text-[10px] text-[#5F6168] mb-1">
              Community
            </div>
            <div className="font-display text-xl font-bold">Join the LinkedIn group</div>
          </div>
        </a>
      </div>
      <button
        type="button"
        onClick={onReset}
        data-testid="gi-reset-btn"
        className="mt-6 font-mono-label text-[11px] text-[#5F6168] hover:text-[#0F1117] transition-colors"
      >
        ← Submit another
      </button>
    </div>
  );
}
